import { PersonaCard } from "@/components/blocks/persona-card";
import { QAItem } from "@/components/blocks/qa-item";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  adcopy_qas,
  getPersonasFromQAs,
  getQAsByPersonaId,
  Persona,
  QA,
} from "@/lib/constants";
import { useEffect, useState } from "react";

export function AdCopyEvalDemo() {
  // Sample ad copy to evaluate
  const adCopy = {
    headline: "Meet Your Perfect Sip. All Season Long.",
    subheadline: "The Spring 2025 Collection is Here",
    bodyCopy:
      "From sunrise coffee to evening refreshment, our new collection keeps your drinks just the way you like them. Textured designs in spring pastels with our signature pattern. Available for a limited time.",
    ctaPrimary: "Shop Now",
    ctaSecondary: "Find a Store",
    targetAudience: "Trend-conscious millennials and Gen Z",
  };

  // Analysis data for the ad copy
  const strengthPoints = [
    {
      category: "Headline",
      description:
        "Short, evocative, and benefits-focused headline creates an emotional connection",
    },
    {
      category: "Tone",
      description:
        "Conversational and friendly tone aligns perfectly with the brand voice",
    },
    {
      category: "Clarity",
      description:
        "Clear value proposition that emphasizes both function and aesthetics",
    },
  ];

  const improvementAreas = [
    {
      category: "CTA Specificity",
      description:
        "Primary CTA could be more specific, e.g., 'Shop The Collection'",
    },
    {
      category: "Urgency",
      description: "Could strengthen scarcity messaging beyond 'limited time'",
    },
  ];

  const kpiPredictions = [
    {
      metric: "Click-through Rate",
      prediction: "4.8%",
      benchmark: "3.2%",
      performance: "Above Average",
    },
    {
      metric: "Conversion Rate",
      prediction: "2.3%",
      benchmark: "1.7%",
      performance: "Above Average",
    },
    {
      metric: "Engagement",
      prediction: "5.7%",
      benchmark: "4.1%",
      performance: "Excellent",
    },
    {
      metric: "Brand Recall",
      prediction: "62%",
      benchmark: "48%",
      performance: "Very Good",
    },
  ];

  const [qaData, setQaData] = useState<QA[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(
    null
  );
  const [filteredQAs, setFilteredQAs] = useState<QA[]>([]);

  const handlePersonaClick = (personaId: number) => {
    setSelectedPersonaId(personaId);
    setFilteredQAs(getQAsByPersonaId(qaData, personaId));
  };

  useEffect(() => {
    // In a real app, you would fetch this data from an API
    // For this example, we'll use the data from the snippet
    const fetchData = async () => {
      try {
        // This would be a fetch call in a real app

        setQaData(adcopy_qas);
        const extractedPersonas = getPersonasFromQAs(adcopy_qas);
        setPersonas(extractedPersonas);

        if (extractedPersonas.length > 0) {
          setSelectedPersonaId(extractedPersonas[0].id);
          setFilteredQAs(
            getQAsByPersonaId(adcopy_qas, extractedPersonas[0].id)
          );
        }

        // setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        // setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        {/* <header className="flex justify-between items-center py-4 mb-6 border-b border-slate-200">
          <div className="text-2xl font-bold text-purple-600">consumr.ai</div>
          <div className="flex gap-4">
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <Bell size={18} />
            </button>
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <User size={18} />
            </button>
          </div>
        </header> */}

        {/* Page Title */}
        <h1 className="text-2xl font-semibold mb-6">
          Ad Copy Creative Evaluation Report
        </h1>
        <div className="inline-block bg-slate-100 rounded-full px-4 py-2 text-sm mb-4">
          Starbucks Spring Collection 2025
        </div>

        {/* Preview Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Ad Copy Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-left">
              <div className="">
                <h2 className="text-xl font-semibold text-[#1f0aae]">
                  {adCopy.headline}
                </h2>
                {adCopy.subheadline && (
                  <p className="text-slate-600">{adCopy.subheadline}</p>
                )}
              </div>
              {adCopy.bodyCopy && (
                <p className="font-medium text-gray-800">{adCopy.bodyCopy}</p>
              )}

              {/* <div className="flex gap-3">
                <div className="bg-purple-600 text-white px-4 py-2 rounded-md font-medium">
                  {adCopy.ctaPrimary}
                </div>
                <div className="bg-transparent border border-slate-300 text-slate-700 px-4 py-2 rounded-md font-medium">
                  {adCopy.ctaSecondary}
                </div>
              </div> */}
            </div>
          </CardContent>
        </Card>

        {/* Score Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Clarity</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                8.5<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Persuasiveness</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                7.8<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Brand Alignment</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                9.2<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Overall Score</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                8.5<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/*  */}
        <div className="container mx-auto py-8 px-4">
          <h3 className="text-xl font-bold mb-8 text-center">
            AI Twin Responses
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {personas.map((persona) => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                isSelected={selectedPersonaId === persona.id}
                onClick={() => handlePersonaClick(persona.id)}
              />
            ))}
          </div>

          {selectedPersonaId && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">
                Responses History -{""}{" "}
                {personas.find((p) => p.id === selectedPersonaId)?.name}
              </h2>

              {filteredQAs.length > 0 ? (
                <div>
                  {filteredQAs.map((qa, index) => (
                    <QAItem key={index} qa={qa} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  No Q&A history available for this persona.
                </p>
              )}
            </div>
          )}
        </div>
        {/*  */}

        {/* Analysis Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Copy Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              This ad copy effectively balances emotional appeal with product
              information. The headline creates an immediate connection, while
              the body copy clearly communicates both functional benefits and
              aesthetic appeal. The messaging aligns well with the visual appeal
              of the product collection.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <h3 className="font-semibold mb-3">Strengths</h3>
                {strengthPoints.map((point, index) => (
                  <div key={index} className="bg-slate-100 rounded-lg p-3 mb-3">
                    <div className="inline-block bg-green-500 text-white px-2 py-1 rounded text-xs mb-2">
                      {point.category}
                    </div>
                    <p>{point.description}</p>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="font-semibold mb-3">Areas for Improvement</h3>
                {improvementAreas.map((area, index) => (
                  <div key={index} className="bg-slate-100 rounded-lg p-3 mb-3">
                    <div className="inline-block bg-amber-500 text-white px-2 py-1 rounded text-xs mb-2">
                      {area.category}
                    </div>
                    <p>{area.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Predictions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Predicted Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Metric
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Prediction
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Benchmark
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Performance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {kpiPredictions.map((kpi, index) => (
                    <tr key={index} className="text-left">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                        {kpi.metric}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {kpi.prediction}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                        {kpi.benchmark}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <Badge
                          className={
                            kpi.performance === "Excellent"
                              ? "bg-green-500"
                              : kpi.performance === "Very Good"
                                ? "bg-green-400"
                                : kpi.performance === "Above Average"
                                  ? "bg-blue-500"
                                  : kpi.performance === "Average"
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                          }
                        >
                          {kpi.performance}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Linguistic Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Ad Component</p>
                <p className="font-medium">Headline + Body + CTA</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Word Count</p>
                <p className="font-medium">28 Words</p>{" "}
                {/* Calculated from mock adCopy */}
              </div>
              <div>
                <p className="text-sm text-slate-500">Readability (FKGL)</p>
                <p className="font-medium">~7.0 (Easy)</p> {/* Mock Value */}
              </div>
              <div>
                <p className="text-sm text-slate-500">Sentiment</p>
                <p className="font-medium text-green-600">Positive</p>{" "}
                {/* Mock Value */}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="mb-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="recommendations">
              <AccordionTrigger className="px-6 py-4">
                <CardTitle className="text-lg font-semibold">
                  Recommendations
                </CardTitle>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="font-semibold mb-2">CTA Enhancement</div>
                      <p>
                        Change "Shop Now" to "Shop The Collection" to increase
                        specificity and connection to the advertised products.
                      </p>
                    </div>

                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="font-semibold mb-2">Urgency Boost</div>
                      <p>
                        Strengthen scarcity message by adding more specific
                        time-bound language, such as "Available until May 31" or
                        "While supplies last."
                      </p>
                    </div>

                    <div className="bg-slate-100 rounded-lg p-3">
                      <div className="font-semibold mb-2">
                        Feature Highlight
                      </div>
                      <p>
                        Consider adding one specific functional benefit (e.g.,
                        "Keeps drinks cold for 12 hours") to strengthen the
                        value proposition.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Target Audience Match */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Target Audience Match
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg">
              <div>
                <div className="text-sm text-slate-500 mb-1">
                  Target Audience
                </div>
                <div className="font-medium">{adCopy.targetAudience}</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500 mb-1">Match Score</div>
                <div className="text-xl font-bold text-green-600">92%</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {/* <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" className="flex items-center gap-2">
            <Download size={16} />
            Download Report
          </Button>
          <Button className="flex items-center gap-2">
            <Share size={16} />
            Share Report
          </Button>
        </div> */}
      </div>
    </div>
  );
}
